import React from "react";
import { Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import { RootState } from "../../store/store";
import { setProfileLoadError } from "../../store/slices/userSlice";
import DashboardPage from "../Dashboard/DashboardPage";
import { canAccessDashboardAnalytics } from "../Navigation/constants/sidebarRoleAccess";
import {
  BusinessType,
  PAGE_PORTAL_EDUCATION,
  PAGE_PORTAL_INVENTORY,
  useKivunovaTranslation,
  usePlatformConfig
} from "@kivunova/kivufrontendcommon";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { useGetProfile } from "../../hooks/api/useProfile";

/**
 * Portal index (`/portal`): dashboard when identity analytics `DASHBOARD_ROLES` applies;
 * otherwise inventory (operational default).
 */
const PortalHomePage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const dispatch = useDispatch();
  const profileData = useSelector(
    (state: RootState) => state.profile.profileData
  );
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const profileLoadError = useSelector(
    (state: RootState) => state.user.profileLoadError
  );
  const getProfileMutation = useGetProfile();
  const { config } = usePlatformConfig();
  const role = profileData?.role ?? userDetails?.role;
  const hasUser = Boolean(profileData?.id ?? userDetails?.id);

  const handleRetry = () => {
    dispatch(setProfileLoadError(false));
    getProfileMutation.mutate(undefined, {
      onError: () => dispatch(setProfileLoadError(true))
    });
  };

  if (!hasUser) {
    if (profileLoadError) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
            p: 3,
            gap: 2
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {t(
              "portal-home-load-error",
              "Failed to load your profile. Please check your connection and try again."
            )}
          </Typography>
          <Button
            variant="contained"
            onClick={handleRetry}
            disabled={getProfileMutation.isPending}
          >
            {t("retry", "Retry")}
          </Button>
        </Box>
      );
    }
    return (
      <CommonLoader loadingMessage={t("portal-home-loading", "Loading...")} />
    );
  }

  if (!role || !canAccessDashboardAnalytics(role)) {
    const defaultPath =
      config?.businessType === BusinessType.EDUCATION
        ? PAGE_PORTAL_EDUCATION
        : PAGE_PORTAL_INVENTORY;
    return <Navigate to={defaultPath} replace />;
  }

  return <DashboardPage />;
};

export default PortalHomePage;
